import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MessageCircle } from "lucide-react";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";
import { Header } from "@/components/site/Header";
import { FaqItem } from "@/pages/FaqPage";
import { REFUND_POLICY, REFUND_POLICY_INTRO, FAQ_REFUND, WHATSAPP_NUMBER } from "@/lib/data";

export default function RefundPolicyPage() {
  const [openIdx, setOpenIdx] = useState(-1);

  return (
    <div className="relative min-h-screen text-white">
      <ParallaxBackground />
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-8" data-testid="refund-page">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono-cap mb-3 flex items-center gap-2 text-[11px] text-neon-cyan">
            <ShieldCheck className="h-4 w-4" /> Pahlawan Championship · Official Policy
          </p>
          <h1 className="font-display text-4xl leading-[0.95] sm:text-6xl">
            REFUND & <span className="bg-gradient-to-r from-violet-400 via-cyan-300 to-green-400 bg-clip-text text-transparent">RETURN</span> POLICY
          </h1>
          <p className="mt-5 max-w-xl font-body text-sm leading-relaxed text-slate-400" data-testid="refund-intro">
            {REFUND_POLICY_INTRO}
          </p>
        </motion.div>

        <div className="mt-10 space-y-4">
          {REFUND_POLICY.map((section, i) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="rounded-3xl border border-white/10 bg-surface/80 p-6 sm:p-7"
              data-testid={`refund-section-${i + 1}`}
            >
              <div className="mb-4 flex items-baseline gap-3">
                <span className="font-display text-2xl text-neon-pink/60">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-xl text-white">{section.title}</h2>
              </div>
              <ul className="space-y-2.5">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-2.5 font-body text-sm leading-relaxed text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-cyan" /> {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-14">
          <p className="font-mono-cap mb-4 text-[11px] text-neon-cyan">Refund — Frequently Asked Questions</p>
          <div className="space-y-3" data-testid="refund-faq">
            {FAQ_REFUND.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center gap-3 rounded-3xl border border-neon-pink/30 bg-neon-pink/5 p-6 text-center" data-testid="refund-contact">
          <p className="font-sub text-sm font-bold text-white">Still have questions about refunds?</p>
          <p className="font-body text-xs text-slate-400">Send a written request to pahlawan.muaythai@gmail.com or contact us via WhatsApp.</p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            className="mt-1 flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-6 py-3 font-mono-cap text-[11px] text-white"
            data-testid="refund-whatsapp-btn"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp Support
          </a>
        </div>
      </main>
    </div>
  );
}
